import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GameVariant, Color, GameStatus } from '@draughts/shared';
import type { BoardState, Move, UserPuzzleStats } from '@draughts/shared';
import { usePuzzleStore } from '../../store/puzzle-store';
import { useSchermoSveglio } from '../../hooks/useSchermoSveglio';
import { useBloccoLente } from '../../hooks/useBloccoLente';
import { useGameStore } from '../../store/game-store';
import { fenToBoardState, getAllLegalMoves, applyMove } from '@draughts/core';
import { fetchPuzzleStats, getPuzzleHint } from '../../services/puzzle-api';
import { Flame, RotateCcw, ChevronLeft, ChevronRight, HelpCircle, Share2, UserPlus, Cpu } from 'lucide-react';
import GameBoard from '../game/GameBoard';
import CorniceDamiera from '../shared/CorniceDamiera';
import DashboardPlayAIModal, { type AIDifficulty } from '../dashboard/DashboardPlayAIModal';
import { shareDailyPuzzle } from '../../utils/share';
import PuzzleUnavailable from './PuzzleUnavailable';
import type { LocalPuzzle } from '../../data/demo-puzzle';
import { tempiDellaSoluzione } from './solution-playback';
import { SOLUTION_STEP_MS } from '../../constants/timing';
import './PuzzlePage.css';

// Non offrire "Continua contro AI" per posizioni ~decise: servono almeno
// 4 pezzi perché ci sia una partita vera da giocare.
const MIN_CONTINUE_PIECES = 4;

interface PuzzlePageProps {
  onBack: () => void;
  initialPuzzleId?: number;
  initialVariant?: string;
  isDaily?: boolean;
  /** Practice replay: validate moves locally/server-side without affecting rating. */
  practice?: boolean;
  /**
   * Fully client-side demo puzzle (landing "Risolvi"): validated in-browser with
   * no auth/server. When set it takes precedence over initialPuzzleId.
   */
  localPuzzle?: LocalPuzzle;
  /** Conversion CTA: open the register (or, for a guest, upgrade) modal. */
  onRegister?: () => void;
  /**
   * Offer the Share button once the daily puzzle is solved. Registered-only:
   * the link it produces opens a registered-only puzzle, so handing it to a
   * guest would only send their friends into the sign-up gate. Passed in rather
   * than read from the auth store because this component is also rendered by
   * the dev preview and by the anonymous demo sheet.
   */
  canShare?: boolean;
  /**
   * Solved puzzles offer a "keep playing vs the AI" button that hands the reached
   * position off to a real local-game screen. The host (App) seeds the game store
   * from `board` (human plays `humanColor`, chosen `difficulty`) and navigates to
   * the game screen. Omitted → the button is hidden (e.g. dev preview).
   */
  onContinueVsAI?: (board: BoardState, humanColor: Color, difficulty: AIDifficulty) => void;
  /**
   * Rendered inside a full-screen overlay sheet (DashboardPuzzleModal) rather
   * than as the standalone 'puzzle' screen. The sheet owns its own scroll
   * container, so PuzzlePage must NOT scroll `<body>` — doing so also scrolls
   * the page behind the overlay (e.g. the landing) to the top.
   */
  embedded?: boolean;
}

export default function PuzzlePage({ onBack, initialPuzzleId, initialVariant, isDaily = false, practice = false, localPuzzle, onRegister, canShare = false, onContinueVsAI, embedded = false }: PuzzlePageProps) {
  const { t, i18n } = useTranslation();
  const isDemo = !!localPuzzle;
  /* La lente (il pinch a molla di `useLente`) qui non deve fare nulla: la
     scacchiera e' gia' dimensionata sulla viewport, non c'e' un dettaglio
     piccolo che l'ingrandimento sveli, e un secondo dito che fa saltare la
     vista mentre si trascina un pezzo e' solo un modo per sbagliare mossa. */
  useBloccoLente();
  const {
    puzzle, status, feedback, ratingChange, newRating, puzzleRating,
    opponentMove, solution,
    loadNextPuzzle, startPuzzle, loadLocalPuzzle, localHint, submitMove, giveUp, replayPuzzle, reset, clearFeedback,
  } = usePuzzleStore();

  /*
   * Lo schermo resta acceso mentre il problema e' aperto.
   *
   * Davanti a un problema si guarda e si pensa, anche per minuti, senza toccare
   * niente: e' la stessa situazione dell'orologio da tavolo, e lo spegnimento
   * automatico la interrompe a meta'. Si spegne da se' appena il problema e'
   * risolto o sbagliato, perche' da li' in poi c'e' un riepilogo da leggere e
   * un bottone da premere.
   *
   * Sotto la lettura dello store e non in cima come `useBloccoLente`: quello
   * non prende argomenti, questo si'. Il primo ritorno anticipato e' molto piu'
   * giu', quindi l'ordine degli hook regge.
   */
  useSchermoSveglio(status === 'playing');

  const syncFromExternal = useGameStore(s => s.syncFromExternal);
  // Serve a uscire dalla revisione quando si carica un puzzle nuovo: vedi sotto.
  const reviewToIndex = useGameStore(s => s.reviewToIndex);
  const gameStoreMakeMove = useGameStore(s => s.makeMove);
  const currentTurn = useGameStore(s => s.currentTurn);

  const variantStr = initialVariant || 'italian';

  // Solved puzzles offer "keep playing vs the AI" from the reached position: this
  // opens the difficulty picker, then the host navigates to a real game screen.
  const [showContinuePicker, setShowContinuePicker] = useState(false);

  const [stats, setStats] = useState<UserPuzzleStats | null>(null);
  const [solvedSteps, setSolvedSteps] = useState(0);
  // Puzzle hint: the from-square to highlight (the piece to move). Asking for a
  // hint forfeits the rating server-side. We only ever reveal the from-square.
  const [hintSquare, setHintSquare] = useState<number | null>(null);
  const [hintLoading, setHintLoading] = useState(false);
  // Daily-puzzle share: transient "Copied!" feedback after a clipboard fallback.
  const [shareCopied, setShareCopied] = useState(false);
  // Solution review: which step of the line the board is showing (0 = start).
  const [reviewStep, setReviewStep] = useState(0);
  // Whether the review cursor has been initialized for the current finished
  // puzzle. Gates the review-render effect so it never syncs the board to the
  // start position on the completing frame (which flashed the captured pieces
  // back before the cursor jumped to the final step).
  const [reviewReady, setReviewReady] = useState(false);
  /*
   * Il guasto di rete è una cosa DIVERSA da «mossa sbagliata»: la richiesta non è
   * arrivata a destinazione, e la mossa non è stata giudicata da nessuno. Prima non
   * c'era nessun ramo per questo caso e la mossa ottimistica restava sulla damiera
   * come accettata, facendo giudicare la mossa successiva su una posizione che il
   * server non conosce — quindi sbagliata, con la serie azzerata per un errore non
   * commesso.
   */
  const [erroreRete, setErroreRete] = useState(false);
  const autoPlayingRef = useRef(false);
  // The last "settled" board position the player is to move from (start position,
  // or the position after each opponent reply). Used to snap the board back when
  // a wrong move was optimistically animated (e.g. an illegal-for-the-puzzle
  // capture), since wrong moves no longer end the puzzle — the player retries.
  const lastSettledRef = useRef<BoardState | null>(null);

  // Whether the mount effect below has run. The store starts at 'idle', which is
  // also the state a failed load lands in, so the "unavailable" branch must not
  // paint on the very first frame — before anything has even been requested.
  const [loadRequested, setLoadRequested] = useState(false);

  // Load puzzle on mount
  useEffect(() => {
    if (localPuzzle) {
      loadLocalPuzzle(localPuzzle);
    } else if (initialPuzzleId) {
      startPuzzle(initialPuzzleId, practice);
    } else {
      loadNextPuzzle(variantStr);
    }
    setLoadRequested(true);
    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always open this page scrolled to the top. The app scrolls on screen
  // change, but the puzzle loads async right after — re-growing the page — so
  // re-assert the top once the puzzle (and its board) is in. The scroll
  // container is <body>, so reset that (window.scrollTo alone doesn't).
  useEffect(() => {
    // In the overlay sheet the scroll container is `.pz-page` (which mounts at
    // the top on its own); touching `<body>` here would yank the page behind
    // the overlay to the top and lose the visitor's scroll position.
    if (embedded) return;
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [puzzle?.fen, embedded]);

  // Fetch the player's puzzle stats (streak / solved) for the hero — real data.
  // Skipped for the anonymous demo (no auth → would 401, and there is no rating).
  useEffect(() => {
    if (isDemo) return undefined;
    let active = true;
    fetchPuzzleStats().then((s) => { if (active) setStats(s); });
    return () => { active = false; };
  }, [isDemo]);

  // Refresh the stats once the puzzle is finished, so the hero streak/solved
  // update without a reload. The hero number is the DAY streak: it moves on the
  // day's first solve (hint or not) and on a give-up, never backwards. A rated
  // fail only resets the separate "solved in a row" counter.
  useEffect(() => {
    if (isDemo) return undefined;
    if (status !== 'completed' && status !== 'failed') return undefined;
    let active = true;
    fetchPuzzleStats().then((s) => { if (active && s) setStats(s); });
    return () => { active = false; };
  }, [status, isDemo]);

  // Sync puzzle FEN to game store when puzzle changes
  useEffect(() => {
    if (!puzzle) return;
    setSolvedSteps(0);
    setHintSquare(null);
    try {
      const gameVariant = puzzle.variant.toUpperCase() as keyof typeof GameVariant;
      const boardState = fenToBoardState(puzzle.fen, GameVariant[gameVariant] ?? GameVariant.INTERNATIONAL);
      lastSettledRef.current = boardState;
      /*
       * `reviewToIndex(null)` prima del sync.
       *
       * `syncFromExternal` NON esce dalla modalità revisione, ed è deliberato: gira
       * a ogni `game:update`, e azzerare la revisione lì butterebbe fuori dalla
       * lista mosse chiunque a ogni mossa dell'avversario. Il contratto è che il
       * chiamante dichiari di stare entrando in un contesto nuovo — ed è quello che
       * fanno tutti gli altri sei chiamanti (`multiplayer-store` in quattro punti,
       * `spectator-store` in due, `EditorAnalysisOverlay`). Questo era l'unico che
       * lo ometteva: chi apriva un puzzle nuovo mentre guardava indietro la
       * soluzione del precedente si trovava davanti una damiera ferma sul passato,
       * con le proprie mosse che non si vedevano.
       */
      reviewToIndex(null);
      // Non e' una mossa appena giocata, e' il sipario che si alza: niente da rigiocare.
      syncFromExternal(boardState, { replay: false });
    } catch {
      // Invalid FEN
    }
  }, [puzzle, syncFromExternal, reviewToIndex]);

  // Flash the 'incorrect' overlay briefly, then clear it. A wrong move no longer
  // ends the puzzle (the player retries), so the "Not quite" banner must not stay
  // stuck on screen — it shakes once (~pzFeedbackShake) and disappears.
  useEffect(() => {
    if (feedback !== 'incorrect' || status !== 'playing') return undefined;
    const timer = setTimeout(() => clearFeedback(), 900);
    return () => clearTimeout(timer);
  }, [feedback, status, clearFeedback]);

  // Handle opponent's response move (auto-play after correct user move)
  useEffect(() => {
    if (!opponentMove) return;

    const timer = setTimeout(() => {
      const state = useGameStore.getState();
      const boardState: BoardState = {
        pieces: state.pieces,
        currentTurn: state.currentTurn,
        status: state.status,
        variant: state.variant,
        moveHistory: state.moveHistory,
        halfMoveClock: state.halfMoveClock,
        positionHistory: state.positionHistory ?? [],
      };

      const allMoves = getAllLegalMoves(boardState);
      const matched = allMoves.find(m => m.from === opponentMove.from && m.to === opponentMove.to);
      if (matched) {
        const newBoard = applyMove(boardState, matched);
        lastSettledRef.current = newBoard;
        syncFromExternal(newBoard);
      }
      clearFeedback();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opponentMove]);

  // Reset review readiness whenever a new attempt starts, so the next finish
  // re-initializes the cursor before the board is reconstructed.
  useEffect(() => {
    if (status !== 'completed' && status !== 'failed') setReviewReady(false);
  }, [status]);

  /**
   * La linea della soluzione, risolta UNA volta sola.
   *
   * `boards[0]` è la posizione iniziale, `boards[i]` quella dopo i mosse; `moves[i]` è la
   * mossa i+1, completa — con il `path`, che il server NON manda (la soluzione arriva come
   * `{ from, to }`). Ed è proprio il `path` la ragione di questo memo: senza, l'autoplay
   * qui sotto non ha modo di sapere quanto dura una mossa, e infatti per un periodo dava a
   * tutte gli stessi sei decimi di secondo — comprese le prese multiple, che ne avrebbero
   * voluti di più e comparivano tutte in un fotogramma.
   *
   * Prima la stessa camminata stava in due posti (la revisione, rifatta da capo a OGNI
   * passo del cursore, e `continuation`) e in nessuno dei due il risultato arrivava dove
   * serviva.
   */
  const lineaSoluzione = useMemo<{ boards: BoardState[]; moves: Move[] } | null>(() => {
    if (!puzzle || !solution || solution.length === 0) return null;
    try {
      const gv = puzzle.variant.toUpperCase() as keyof typeof GameVariant;
      let board = fenToBoardState(puzzle.fen, GameVariant[gv] ?? GameVariant.INTERNATIONAL);
      const boards: BoardState[] = [board];
      const moves: Move[] = [];
      for (const mv of solution) {
        const matched = getAllLegalMoves(board).find((m) => m.from === mv.from && m.to === mv.to);
        if (!matched) break;
        board = applyMove(board, matched);
        moves.push(matched);
        boards.push(board);
      }
      return { boards, moves };
    } catch {
      return null; // FEN o soluzione non valide: la pagina si comporta come senza linea
    }
  }, [puzzle, solution]);

  // Solution review: render the board at the current step (start position + the
  // first `reviewStep` moves of the line). Active once the puzzle is done AND the
  // cursor has been initialized (reviewReady) — otherwise a clean solve would
  // briefly sync to the start position (reviewStep 0) and flash captured pieces.
  useEffect(() => {
    const done = status === 'completed' || status === 'failed';
    if (!done || !lineaSoluzione || !reviewReady) return;
    const board = lineaSoluzione.boards[Math.min(reviewStep, lineaSoluzione.boards.length - 1)];
    if (!board) return;
    // Niente `replay: false`, e non serve distinguere la direzione: un passo AVANTI allunga
    // la storia di uno e la presa multipla si percorre come ovunque, mentre tornare
    // indietro l'accorcia e saltare in fondo l'allunga di N — e `detectExternalCapture`
    // scarta entrambi da sé. L'unico caso che si anima è l'unico che deve.
    syncFromExternal(board);
  }, [reviewStep, reviewReady, status, lineaSoluzione, syncFromExternal]);

  // On give-up / wrong move, auto-play the line once (advance the review step on
  // a timer). On a clean solve, DON'T replay — keep the final position; the
  // review controls still let the player step back through it. Manual navigation
  // cancels the auto-play (autoPlayingRef).
  useEffect(() => {
    if (!solution || solution.length === 0) return undefined;
    if (status === 'failed') {
      autoPlayingRef.current = true;
      setReviewStep(0);
      setReviewReady(true);
      // I tempi non sono più `600 * i`: ogni presa multipla si porta dietro i suoi salti e
      // sposta in avanti tutto quello che viene dopo. Su una linea senza prese multiple
      // tornano identici a prima — vedi tempiDellaSoluzione.
      const tempi = tempiDellaSoluzione(lineaSoluzione?.moves ?? []);
      const timers: ReturnType<typeof setTimeout>[] = [];
      for (let i = 1; i <= solution.length; i++) {
        const quando = tempi[i - 1] ?? SOLUTION_STEP_MS * i;
        timers.push(setTimeout(() => { if (autoPlayingRef.current) setReviewStep(i); }, quando));
      }
      return () => { timers.forEach(clearTimeout); };
    }
    if (status === 'completed') {
      autoPlayingRef.current = false;
      setReviewStep(solution.length);
      setReviewReady(true);
    }
    return undefined;
  }, [status, solution, lineaSoluzione]);

  // Intercept game store moves for puzzle validation
  const handlePuzzleMove = useCallback((move: Move) => {
    if (status !== 'playing') return;
    setErroreRete(false); // ogni nuovo tentativo riparte senza l'avviso precedente
    setHintSquare(null); // the position is about to change — the hint is stale
    // Apply the player's move optimistically for instant feedback (like online
    // multiplayer) instead of waiting for the server round-trip. For captures
    // GameBoard already applied the move locally, so makeMove no-ops here (guard
    // on an empty from-square). The server then confirms; a wrong move snaps back.
    gameStoreMakeMove(move);
    submitMove(move.from, move.to).then((result) => {
      if (result?.correct) {
        if (!result.completed) setSolvedSteps((s) => s + 1);
        return;
      }
      if (result && !result.correct) {
        // Wrong move: the puzzle stays playable. Snap the board back to the last
        // settled position (the optimistic move — simple or capture — is undone).
        // Si torna indietro, non avanti: il rilevatore lo capirebbe da solo (la storia
        // si accorcia), ma l'intento e' meglio scritto che dedotto.
        if (lastSettledRef.current) syncFromExternal(lastSettledRef.current, { replay: false });
        return;
      }
      /*
       * `result` è `null`: la richiesta NON è arrivata a destinazione (nonce
       * mancante, o il `catch` dello store). Non è una mossa sbagliata.
       *
       * I due rami sopra erano entrambi guardati e non c'era nessun `else`: con
       * `null` la mossa ottimistica restava sulla damiera come se fosse stata
       * accettata, e la mossa SUCCESSIVA veniva giudicata su una posizione che il
       * server non conosce — quindi sbagliata, con `solvedInARow` azzerato e
       * l'eventuale calo di rating incassato. Un blip di rete costava la serie
       * per un errore non commesso.
       *
       * Si riporta la damiera al vero e si segnala il guasto DI RETE, che è una
       * cosa diversa da «non quite».
       */
      if (lastSettledRef.current) syncFromExternal(lastSettledRef.current, { replay: false });
      setErroreRete(true);
    });
  }, [status, submitMove, gameStoreMakeMove, syncFromExternal]);

  // Ask the server for a hint (the from-square of the next move). Forfeits the
  // rating for this attempt; we only highlight the piece, never the destination.
  const requestHint = useCallback(async () => {
    if (status !== 'playing' || hintLoading) return;
    if (isDemo) {
      // Demo: compute the hint in-browser from the embedded solution.
      const local = localHint();
      if (local) setHintSquare(local.from);
      return;
    }
    setHintLoading(true);
    const result = await getPuzzleHint();
    setHintLoading(false);
    if (result) setHintSquare(result.from);
  }, [status, hintLoading, isDemo, localHint]);

  // Manual solution navigation (cancels the auto-play).
  const reviewPrev = () => { autoPlayingRef.current = false; setReviewStep((s) => Math.max(0, s - 1)); };
  const reviewNext = () => { autoPlayingRef.current = false; setReviewStep((s) => Math.min(solution?.length ?? 0, s + 1)); };

  const isPlayerTurn = puzzle
    ? (puzzle.playerColor === 'white' ? currentTurn === Color.WHITE : currentTurn === Color.BLACK)
    : false;

  // Weekday + month follow the UI language (the hero showed an English date in
  // every locale). Falls back to the browser default if the tag is unknown.
  const dateLabel = useMemo(() => {
    const d = new Date();
    const locale = i18n.language || undefined;
    const weekday = d.toLocaleDateString(locale, { weekday: 'long' });
    const month = d.toLocaleDateString(locale, { month: 'short' });
    return `${weekday} · ${d.getDate()} ${month} ${d.getFullYear()}`;
  }, [i18n.language]);

  const isDone = status === 'completed' || status === 'failed';
  const isSolved = status === 'completed';

  // The board AFTER the whole solution line is played out — the position from
  // which the player can keep going vs the AI (via the "Continue vs AI" button).
  // Recomputed from the solution (not read off the live board, since the review
  // controls can rewind the cursor). `null` unless solved AND the final position
  // is worth continuing: still playable AND not a trivially-decided position
  // (fewer than 4 pieces on the board — see MIN_CONTINUE_PIECES).
  const continuation = useMemo<BoardState | null>(() => {
    if (!isSolved || !lineaSoluzione) return null;
    const board = lineaSoluzione.boards[lineaSoluzione.boards.length - 1];
    if (!board) return null;
    const stillAGame =
      board.status === GameStatus.PLAYING && board.pieces.length >= MIN_CONTINUE_PIECES;
    return stillAGame ? board : null;
  }, [isSolved, lineaSoluzione]);

  const humanColor = puzzle?.playerColor === 'black' ? Color.BLACK : Color.WHITE;

  const handleShare = useCallback(async () => {
    const result = await shareDailyPuzzle(puzzle?.variant ?? variantStr, t);
    if (result === 'copied') {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  }, [puzzle?.variant, variantStr, t]);
  const turnColor = puzzle?.playerColor === 'black' ? t('puzzle.black', 'Black') : t('puzzle.white', 'White');
  const moveCount = puzzle?.moveCount ?? 0;
  // The unified activity streak (consecutive DAYS with a game or a solved
  // puzzle) — the same number the home StreakCard shows. Deliberately not
  // optimistically incremented per solve the way the old puzzles-in-a-row
  // counter was: a day streak moves at most once a day, so a +1 would be wrong
  // from the second puzzle onward. The effect above refetches /puzzles/stats on
  // completion, which is what makes it move on the day's first solve.
  const streakValue = stats?.dayStreak ?? 0;

  // Solution-progress dots. Rendered in the desktop `.pz-move-progress` card AND,
  // on mobile, inside the turn card (label-less) — so keep the logic in one place.
  const renderProgressDots = () =>
    Array.from({ length: moveCount }, (_, i) => {
      const cls = isSolved || i < solvedSteps ? 'pz-done' : (i === solvedSteps && status === 'playing' ? 'pz-now' : '');
      return <span key={i} className={`pz-dot ${cls}`}></span>;
    });

  if (status === 'loading' && !puzzle) {
    return (
      <div className="pz-page">
        <div className="pz-inner">
          <div className="pz-loading">{t('puzzle.loading', 'Loading puzzle…')}</div>
        </div>
      </div>
    );
  }

  // Nothing loaded: /puzzles/:id/start 401'd (no session), 404'd, or the network
  // failed — the store swallows all of those into 'idle' (puzzle-store.ts). This
  // used to fall through to the full layout around a null puzzle, i.e. the game
  // store's default starting position on the board with inert controls.
  if (loadRequested && status === 'idle' && !puzzle) {
    return (
      <div className="pz-page">
        <div className="pz-inner">
          <PuzzleUnavailable
            reason="session"
            onRetry={initialPuzzleId ? () => startPuzzle(initialPuzzleId, practice) : undefined}
            onRegister={onRegister}
            onHome={onBack}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pz-page">
      <div className="pz-inner">

        {/* HERO */}
        <section className="pz-card pz-hero">
          <div className="pz-hero-deco" aria-hidden="true">
            <div className="pz-hero-king"></div>
          </div>
          <div className="pz-hero-center">
            <div className="pz-hero-eyebrow">{dateLabel}</div>
            <h1 className="pz-hero-title">
              {isDaily ? <>Daily <span className="pz-accent">puzzle</span></> : <span className="pz-accent">Puzzle</span>}
            </h1>
          </div>
          {!isDemo && (
            <div className="pz-hero-streak">
              <div className="pz-streak-flame" aria-hidden="true"><Flame strokeWidth={2.4} /></div>
              <div className="pz-streak-text">
                <div className="pz-streak-val">{streakValue}</div>
                {/* "Streak" is a brand term, kept in English across locales —
                    same literal as StreakCard.tsx, so the two surfaces can't
                    show one number under two names. Locked by
                    packages/web/src/__tests__/i18n-brand-terms.test.ts. */}
                <div className="pz-streak-lbl">Streak</div>
              </div>
            </div>
          )}
        </section>

        {/* PLAY */}
        <section className="pz-card pz-play">
          <div className="pz-board-col">
            <CorniceDamiera className="pz-board-wrap">
              <GameBoard
                isOnlineMode={status === 'playing'}
                onOnlineMove={handlePuzzleMove}
                isMyTurn={isPlayerTurn && status === 'playing'}
                flipped={puzzle?.playerColor === 'black'}
                hintSquare={hintSquare}
                // In un problema l'insieme dei pezzi che possono muovere è la casella di
                // partenza della soluzione: l'aiuto si chiede, non si riceve sbagliando.
                movableHintEnabled={false}
              />
            </CorniceDamiera>
            {/* Feedback strip BELOW the board (never over the pieces). Reserved
                height keeps the layout from jumping when it appears/disappears. */}
            <div className="pz-feedback-slot">
              {feedback === 'correct' && status !== 'completed' && (
                <div className="pz-feedback pz-correct">{t('puzzle.correct', 'Correct!')}</div>
              )}
              {feedback === 'incorrect' && (
                <div className="pz-feedback pz-incorrect">{t('puzzle.incorrect', 'Not quite')}</div>
              )}
              {/* Guasto di rete: non è un errore del giocatore, e il testo lo dice. */}
              {erroreRete && (
                <div className="pz-feedback pz-incorrect" role="alert">{t('puzzle.networkError')}</div>
              )}
            </div>
          </div>

          <aside className="pz-side">
            {/* Turn / result */}
            <div className={`pz-turn-card ${isSolved ? 'pz-solved' : status === 'failed' ? 'pz-failed' : ''}`}>
              <div className="pz-turn-main">
                {isDone ? (
                  <>
                    <div className="pz-eyebrow">{isSolved ? t('puzzle.result.solvedEyebrow', 'Solved') : t('puzzle.result.failedEyebrow', 'Solution')}</div>
                    <div className="pz-turn-line">
                      {isSolved
                        ? <>{t('puzzle.result.nice', 'Nice')} <span className="pz-who">{t('puzzle.result.solving', 'solving.')}</span></>
                        : <>{t('puzzle.result.theLine', 'Here is the')} <span className="pz-who">{t('puzzle.result.line', 'line.')}</span></>}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pz-eyebrow">{t('puzzle.yourTurn', 'Your turn')}</div>
                    <div className="pz-turn-line"><span className="pz-who">{turnColor}</span> {t('puzzle.toPlayAndWin', 'to play and win')}</div>
                  </>
                )}
              </div>
              {/* Streak chip — shown next to the turn text on mobile (the hero,
                  which carries the streak on desktop, is hidden there). */}
              {!isDemo && (
                <div className="pz-turn-streak" aria-label="Streak">
                  <Flame size={16} strokeWidth={2.4} aria-hidden="true" />
                  <span className="pz-turn-streak-val">{streakValue}</span>
                </div>
              )}
              {/* Progress dots at the bottom of the card — mobile only (CSS). */}
              {moveCount > 0 && <div className="pz-dots pz-turn-dots">{renderProgressDots()}</div>}
            </div>

            {/* Stats: puzzle rating + variant (or rating change once done).
                Hidden in the anonymous demo — there is no rating for a guest. */}
            {!isDemo && (
            <div className="pz-stats">
              <div className="pz-stat">
                <div className="pz-lbl">{t('puzzle.puzzleRating', 'Puzzle rating')}</div>
                {isDone && puzzleRating !== null ? (
                  <>
                    <div className="pz-val">{puzzleRating}</div>
                    {puzzle?.rating != null && puzzleRating !== puzzle.rating && (
                      <div className="pz-sub">{puzzleRating > puzzle.rating ? '+' : ''}{puzzleRating - puzzle.rating}</div>
                    )}
                  </>
                ) : (
                  <div className="pz-val">{puzzle?.rating ?? '—'}</div>
                )}
              </div>
              {isDone && ratingChange !== null ? (
                <div className="pz-stat">
                  <div className="pz-lbl">{t('puzzle.ratingChange', 'Rating')}</div>
                  <div className={`pz-val ${ratingChange >= 0 ? 'pz-up' : ''}`}>{ratingChange >= 0 ? '+' : ''}{ratingChange}</div>
                  {newRating !== null && <div className="pz-sub">{t('puzzle.newRating', 'now')} {newRating}</div>}
                </div>
              ) : (
                <div className="pz-stat">
                  <div className="pz-lbl">{t('puzzle.yourRating', 'Your rating')}</div>
                  <div className="pz-val">{stats?.rating ?? '—'}</div>
                </div>
              )}
            </div>
            )}

            {/* Move progress */}
            {moveCount > 0 && (
              <div className="pz-move-progress">
                <div className="pz-mp-lbl">{t('puzzle.solutionMoves', 'Solution')} · {moveCount} {t('puzzle.moves', 'moves')}</div>
                <div className="pz-dots">{renderProgressDots()}</div>
              </div>
            )}

            {/* Actions */}
            <div className="pz-actions">
              {/* Hint + Solution vanish once the puzzle is finished (solved OR the
                  solution was revealed) — replaced by the continue/review CTAs. */}
              {!isDone && (
                <>
                  <button className="pz-btn pz-primary" type="button" onClick={requestHint} disabled={hintLoading}>
                    {t('puzzle.hint', 'Hint')}
                    <span
                      className="pz-btn-help"
                      tabIndex={0}
                      role="img"
                      aria-label={t('puzzle.hintTip', 'A hint only costs you the rating: this puzzle becomes unrated, so you gain no points and lose none. It still counts as solved and keeps your Streak.')}
                      data-tip={t('puzzle.hintTip', 'A hint only costs you the rating: this puzzle becomes unrated, so you gain no points and lose none. It still counts as solved and keeps your Streak.')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HelpCircle size={15} />
                    </span>
                  </button>
                  <button className="pz-btn pz-primary" type="button" onClick={giveUp}>
                    {t('puzzle.solution', 'Solution')}
                    {/* Same help badge as the hint: giving up now keeps the day
                        Streak (the attempt is flagged solution_revealed
                        server-side) but still costs rating, which is not
                        something the button label can convey on its own. */}
                    <span
                      className="pz-btn-help"
                      tabIndex={0}
                      role="img"
                      aria-label={t('puzzle.solutionTip', 'Revealing the solution counts as a failed attempt: you lose rating points, but the day still counts towards your Streak.')}
                      data-tip={t('puzzle.solutionTip', 'Revealing the solution counts as a failed attempt: you lose rating points, but the day still counts towards your Streak.')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HelpCircle size={15} />
                    </span>
                  </button>
                </>
              )}
              {/* Solved & still playable: keep playing the reached position vs the
                  AI on a real game screen. Hidden in the anonymous demo (its only
                  post-solve CTA is the register prompt below). */}
              {!isDemo && isSolved && continuation && onContinueVsAI && (
                <button className="pz-btn pz-primary" type="button" onClick={() => setShowContinuePicker(true)}>
                  <Cpu size={16} aria-hidden="true" />
                  {t('puzzle.continueVsAI', 'Continue vs computer')}
                </button>
              )}
              {isDone && (
                <div className="pz-review">
                  <button className="pz-review-btn" type="button" onClick={replayPuzzle} title={t('puzzle.playAgain', 'Play again')} aria-label={t('puzzle.playAgain', 'Play again')}><RotateCcw /></button>
                  <button className="pz-review-btn" type="button" onClick={reviewPrev} disabled={!solution?.length || reviewStep <= 0} title={t('puzzle.back', 'Back')} aria-label={t('puzzle.back', 'Back')}><ChevronLeft /></button>
                  <button className="pz-review-btn" type="button" onClick={reviewNext} disabled={!solution?.length || reviewStep >= solution.length} title={t('puzzle.forward', 'Forward')} aria-label={t('puzzle.forward', 'Forward')}><ChevronRight /></button>
                </div>
              )}
            </div>

            {/* Share the daily puzzle with a friend once it's solved.
                Registered users only — see `canShare`. */}
            {isDaily && isSolved && canShare && (
              <button className="pz-btn pz-share" type="button" onClick={handleShare}>
                <Share2 size={16} aria-hidden="true" />
                {shareCopied ? t('puzzle.shareCopied', 'Link copied!') : t('puzzle.share', 'Share')}
              </button>
            )}

            {/* Conversion CTA: once the visitor finishes the puzzle, spell out
                what an account gives them and open the register/upgrade modal.
                Solo il DEMO, ed e' la sola forma possibile: su un problema vero
                nessuno arriva qui senza un conto. Ogni strada che porta a
                `status: 'playing'` su un puzzle del server passa da `/next` o
                da `/:id/start`, e quelle rotte sono `requireRegistered` — il
                `catch` di `startPuzzle` riporta a `'idle'`, quindi `isDone` non
                diventa mai vero. C'era una prop `showRegisterCta`, passata
                `isGuest`, per il caso «ospite su problema reale»: quel caso non
                esiste da quando i problemi sono chiusi agli iscritti, e la sua
                copy prometteva di «conservare» un rating e una Streak che un
                ospite non ha. */}
            {isDone && onRegister && isDemo && (
              <div className={`pz-demo-cta${isSolved ? ' pz-demo-cta-solved' : ''}`}>
                <div className="pz-demo-cta-title">
                  {isSolved
                    ? t('puzzle.demo.solvedTitle', 'Nice — solved!')
                    : t('puzzle.demo.trainTitle', 'Want to train every day?')}
                </div>
                <p className="pz-demo-cta-text">
                  {t('puzzle.demo.registerPitch', 'Create a free account: a new puzzle every day, a different one for each variant.')}
                </p>
                <button className="pz-btn pz-primary pz-demo-cta-btn" type="button" onClick={onRegister}>
                  <UserPlus size={16} aria-hidden="true" />
                  {t('puzzle.demo.registerCta', 'Create a free account')}
                </button>
              </div>
            )}
          </aside>
        </section>

      </div>

      {/* Continue-vs-AI difficulty picker. The variant is locked to the puzzle's
          (hidden selector); the host seeds the game from `continuation` and
          navigates to the game screen on confirm. */}
      {continuation && onContinueVsAI && (
        <DashboardPlayAIModal
          isOpen={showContinuePicker}
          hideVariant
          // Il proseguimento riparte dalla posizione risolta, con il colore del puzzle e
          // le regole standard: apertura, colore e modalità qui non hanno nulla da scegliere.
          hideOpening
          hideColor
          hideMode
          defaultVariant={puzzle?.variant}
          defaultDifficulty="medium"
          // Remember the last AI difficulty across opens (shared "last used" key with the
          // dashboard Sfida AI modal). hideVariant keeps the variant locked to the puzzle's,
          // so this only persists/restores the difficulty, never the variant.
          rememberSettings
          onClose={() => setShowContinuePicker(false)}
          onConfirm={(_variant, difficulty) => {
            setShowContinuePicker(false);
            onContinueVsAI(continuation, humanColor, difficulty);
          }}
        />
      )}
    </div>
  );
}
